/**
 * Training indicator. It stays up whenever the screen is a training setup rather
 * than a real boat on real water, so a student glancing at the screen can never
 * take it for a live position on a real chart. That's true in two cases, and the
 * banner covers both: the selected charts are the fictional RYA practice set, or
 * the position is coming from the simulator. Either one on its own is enough to
 * mean "this is not for navigation", so the marker is derived from the actual
 * chart + position-source selection rather than a separate mode flag. It clears
 * the moment we're on real charts AND the device receiver.
 *
 * Kept unobtrusive on purpose: a slim navy pill top-centre, out of the way of the
 * top controls and below the (rare, red) alarm banner, and it never eats taps.
 */
import { useChartPlotterStore } from '@/store';
import { usePositionSourceStore } from '@/store/positionSource';
import { isFictionalProvider } from '@/chart-sources';

export function TrainingBanner() {
  const providerId = useChartPlotterStore((s) => s.chartProviderId);
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  const fictionalCharts = isFictionalProvider(providerId);
  const simPosition = sourceKind === 'sim';
  if (!fictionalCharts && !simPosition) return null;
  // Say why it's up, so the marker is honest about what's not real: the charts,
  // the position, or both.
  const sub = fictionalCharts
    ? simPosition
      ? 'fictional charts & simulated position, not for navigation'
      : 'fictional charts, not for navigation'
    : 'simulated position, not for navigation';
  return (
    <div className="acp-training-banner" role="status" aria-label={`Training mode: ${sub}`}>
      <span className="acp-training-dot" aria-hidden="true" />
      <span className="acp-training-word">TRAINING</span>
      <span className="acp-training-sub">{sub}</span>
    </div>
  );
}
